<ng-container *ngIf="(loading$ | async) === false">
  <ng-container *ngIf="book$ | async">
    <mat-sidenav-container *ngIf="(readerStore.hideReader$ | async) === false" class="sidenav-container">
      <!-- contents drawer -->
      <mat-sidenav
        #leftDrawer
        mode="over"
        hasBackdrop="true"
        class="rdrx-drawer"
        [opened]="tocOpen$ | async"
        (closedStart)="closeLeftDrawer()"
        (openedStart)="contentsMenu?.setFocus()"
      >
        <rdrx-contents-menu
          #contentsMenu
          class="drawer-container"
          role="dialog"
          data-automation-id="contents-menu-dialog"
          aria-labelledby="content-title"
          [attr.aria-modal]="true"
          (closeEvent)="leftDrawer.close()"
        ></rdrx-contents-menu>
      </mat-sidenav>

      <!-- search drawer -->
      <mat-sidenav
        #rightDrawer
        class="search-sidenav rdrx-drawer"
        position="end"
        mode="over"
        hasBackdrop="true"
        [opened]="searchOpen$ | async"
        (closedStart)="closeRightDrawer()"
      >
        <ng-container *ngIf="enableElasticSearch$ | async; then dfaSearch; else coreSearch"></ng-container>
        <ng-template #coreSearch>
          <reader-core-search
            class="drawer-container"
            role="dialog"
            data-automation-id="search-dialog"
            [attr.aria-modal]="true"
            [attr.aria-label]="'search.search' | translate"
            (closeEvent)="rightDrawer.close()"
          ></reader-core-search>
        </ng-template>
        <ng-template #dfaSearch>
          <reader-elastic-search
            class="drawer-container reader-search"
            role="dialog"
            data-automation-id="elastic-search-dialog"
            [attr.aria-modal]="true"
            [loading]="loading$ | async"
            [suggestions]="suggestions$ | async"
            [topics]="topics$ | async"
            [quizzes]="quizzes$ | async"
            [nodes]="nodes$ | async"
            [materialNodes]="materialNodes$ | async"
            [totalResults]="totalResults$ | async"
            (suggestionClicked)="handleSuggestionClicked($event)"
            (searchQueryChanged)="handleSearchQueryChanged($event)"
            (resultClicked)="handleSearchResultClicked($event)"
            (closeEvent)="rightDrawer.close()"
          ></reader-elastic-search>
        </ng-template>
      </mat-sidenav>

      <!-- content -->
      <mat-sidenav-content>
        <!-- only show BETA badge if in FA4 context -->
        <div
          class="reader-layout"
          [class.ai-panel-open]="showAiPanel$ | async"
          >
          <div class="rdrx-content-region">
            <!-- toolbar -->
            <reader-core-toolbar
              *ngIf="navbar$ | async"
              role="region"
              data-automation-id="toolbar-region"
              [attr.aria-label]="'navigation.navigation' | translate"
            ></reader-core-toolbar>

            <div class="rdrx-content-container">
              <!-- alerts -->
              <div class="epub-alerts">
                <mhe-alert
                  *ngIf="(bannerMessage$ | async) && showBannerMessageAlert"
                  data-automation-id="banner-message-alert"
                  id="banner-message"
                  [type]="AlertType.Warning"
                  [smallAlert]="true"
                  [closable]="true"
                  (close)="handleBannerMessageAlertClose()"
                >
                  {{ bannerMessage$ | async }}
                </mhe-alert>

                <mhe-alert
                *ngIf="(showFallbackBannerForEvergreen$ | async)"
                id="invalid-release-version-banner"
                data-automation-id="invalid-release-version-banner"
                [type]="AlertType.Warning"
                [smallAlert]="true"
                [closable]="false"
              >
                {{ 'alert.invalid_releaseId_banner' | translate }}
                 <a data-automation-id="reader-support-link" href="https://www.mheducation.com/support.html" target="_blank">{{ 'alert.invalid_releaseId_banner_link' | translate }}</a>
              </mhe-alert>

                <mhe-alert
                  *ngIf="(readerStore.pdfUrl$ | async) as pdf"
                  data-automation-id="pdf-ready"
                  [type]="AlertType.Info"
                  [smallAlert]="true"
                  [closable]="true"
                  (close)="closePdfAlert()"
                >
                  {{ 'print.file_ready' | translate }}&nbsp;
                  <a
                    href="#"
                    (click)="printPdf($event, pdf.url)"
                    data-automation-id="pdf-link"
                  >
                    {{ pdf.title }}
                  </a>
                </mhe-alert>

                <mhe-alert
                  *ngIf="quickAnnotationEnabled$ | async"
                  data-automation-id="quick-annotation-alert"
                  id="quick-annotation-message"
                  [type]="AlertType.Warning"
                  [smallAlert]="true"
                  [closable]="true"
                  (close)="readerStore.toggleQuickAnnotationEnabled()"
                >
                  {{ 'annotation.quick_annotation_message' | translate }}
                </mhe-alert>

                <mhe-alert *ngIf="assignmentReview$ | async" data-automation-id="assignment-review-alert" [type]="AlertType.Warning" [closable]="false">
                  {{ 'assignment.review_mode' | translate }}
                </mhe-alert>

                <mhe-alert *ngIf="showSessionError$ | async" data-automation-id="session-error-alert" [smallAlert]="true" [type]="AlertType.Error">
                  {{ 'dialog.session_ended_error' | translate }}
                </mhe-alert>

                <mhe-alert
                  *ngIf="documentRequestError$ | async"
                  data-automation-id="document-request-error-alert"
                  [type]="AlertType.Warning"
                  (close)="closeDocumentRequestAlert()"
                >
                  {{ 'dialog.document_request_error' | translate }}
                </mhe-alert>

                <ng-container *ngIf="featureTogglesService.isMaintenanceNotificationEnabled$ | async">
                  <reader-ui-maintenance-alert></reader-ui-maintenance-alert>
                </ng-container>

                <div *ngIf="showAlerts$ | async">
                  <mhe-alert
                    *ngFor="let alertGroup of groupedAlerts$ | async; trackBy: trackByAlertKey"
                    [attr.data-automation-id]="'reader-alert-' + alertGroup.alert.translateKey"
                    [smallAlert]="true"
                    [type]="alertGroup.alert.alertType"
                    (close)="handleAlertDismiss(alertGroup.alert.translateKey)"
                  >
                    {{ alertGroup.alert.translateKey | translate }}
                    <span *ngIf="alertGroup.count > 1" class="alert-count"> (x{{ alertGroup.count }}) </span>
                  </mhe-alert>
                </div>
              </div>

              <!-- epub controls -->
              <reader-core-epub-controls
                *ngIf="showEpubControls$ | async"
                [isFixedLayout]="isFixedLayout$ | async"
                [isDoubleSpread]="isDoubleSpread$ | async"
                [isSinglePageViewOnly] = "isSinglePageViewOnly$ | async"
                [albumMode]="albumMode$ | async"
                [zoomLevel]="epubZoomLevel$ | async"
                [increaseEpubZoomEnabled]="increaseEpubZoomEnabled$ | async"
                [decreaseEpubZoomEnabled]="decreaseEpubZoomEnabled$ | async"
                [enableUpArrow]="zoomMoveUpEnabled$ | async"
                [enableDownArrow]="zoomMoveDownEnabled$ | async"
                [enableLeftArrow]="zoomMoveLeftEnabled$ | async"
                [enableRightArrow]="zoomMoveRightEnabled$ | async"
                [enableTTS]="enableTTS$ | async"
                [showReadSpeakerVoicePick]="showReadSpeakerVoicePick$ | async"
                [isAiAssistOffered]="aiAssistIsOffered$ | async"
                [isAiSummaryOffered]="aiSummaryIsOffered$ | async"
                (toggleAlbumMode)="onToggleAlbumMode()"
                (toggleAiAssistPanel)="onToggleAiAssistPanel()"
                (toggleAiSummaryPanel)="onToggleAiSummaryPanel()"
                (increaseEpubZoomLevel)="onIncreaseEpubZoomLevel()"
                (decreaseEpubZoomLevel)="onDecreaseEpubZoomLevel()"
                (moveEpub)="onMoveZoomedEpub($event)"
              ></reader-core-epub-controls>

              <reader-core-concept-navigator></reader-core-concept-navigator>

              <!-- epub viewer -->
              <reader-core-epub-ui-viewer
                [isDoubleSpread]="isDoubleSpread$ | async"
                [albumMode]="albumMode$ | async"
              >
              </reader-core-epub-ui-viewer>
            </div>

            <!-- footer -->
            <ng-container *ngIf="showFooter$ | async">
              <footer>
                <div class="rdrx-content-container">
                  <div
                    *ngIf="scrubber$ | async"
                    class="scrubber"
                    data-automation-id="scrubber-region"
                    role="region"
                    [attr.aria-label]="'scrubber.chapter_navigation' | translate"
                  >
                    <reader-core-scrubber></reader-core-scrubber>
                  </div>

                  <div
                    *ngIf="assignmentActive$ | async"
                    class="assignment"
                    data-automation-id="assignment-region"
                    role="region"
                    [attr.aria-label]="'assignment.submit_btn' | translate"
                  >
                    <mhe-alert
                      *ngIf="backToAssignmentButton$ | async"
                      data-automation-id="assignment-warning-alert"
                      [type]="AlertType.Warning"
                      [closable]="false"
                      [ngClass]="footer - alert"
                    >
                      {{ 'assignment.warning_page_out_of_range' | translate }}
                    </mhe-alert>

                    <button
                      data-automation-id="reader-submit-assignment-button"
                      *ngIf="(backToAssignmentButton$ | async) === false"
                      mhe-button
                      [purpose]="ButtonPurpose.Primary"
                      [disabled]="
                        (submitAssignmentDisabled$ | async) ||
                        ((this.readerConfigStore.pageStartCfi$ | async) &&
                          (this.readerConfigStore.pageEndCfi$ | async) &&
                          false === (isLastPage$ | async))
                      "
                      (click)="submitAssignment()"
                    >
                      <ng-container *ngIf="(k5$ | async) === false">
                        {{ 'assignment.submit_btn' | translate }}
                      </ng-container>
                      <ng-container *ngIf="k5$ | async">
                        <mat-icon svgIcon="k5:checkmark" class="rdrx-k5-icon"></mat-icon>
                        <span translate> assignment.submit_k5_btn </span>
                      </ng-container>
                    </button>
                    <button
                      data-automation-id="reader-back-assignment-button"
                      [class]="(k5$ | async) ? 'k5-back-assignment-btn' : ''"
                      *ngIf="backToAssignmentButton$ | async"
                      mhe-button
                      [purpose]="ButtonPurpose.Primary"
                      (click)="navigateToPageStart()"
                    >
                      <ng-container *ngIf="(k5$ | async) === false">
                        {{ 'assignment.back_assignment_btn' | translate }}
                      </ng-container>

                      <ng-container *ngIf="k5$ | async">
                        <span translate> assignment.back_assignment_btn </span>
                      </ng-container>
                    </button>
                  </div>
                </div>
              </footer>
            </ng-container>
          </div>

          <div
            class="rdrx-content-ai-tools"
            [class.ai-assist-active]="(showAiAssist$ | async) === true"
            [class.ai-summary-active]="(showAiSummary$ | async) === true"
            [class.ai-assist-show-beta]="(config$ | async)?.topicsEndpoint"
          >
            <ngx-ai-assist
              id="ngx-ai-assist-panel"
              aria-label="AI Assist Panel"
              role="region"
              data-automation-id="ai-assist-panel"
              *ngIf="(aiAssistIsEnabled$ | async) === true"
              [hidden]="(showAiAssist$ | async) === true"
              [panelConfig]="aiAssistPanelConfig | async"
              [userSelection]="annotationForAiAssist$ | async"
              (closeAiAssistPanelEvent)="onCloseAiAssistPanel()"
              (componentIsReady)="onAiAssistIsReady($event)"
              (navigateAiAssistPanelEvent)="onNavFromAiAssistPanel($event)"
            ></ngx-ai-assist>
            @if (showAiSummary$ | async) {
              <div
                id="ngx-ai-summary-panel"
                class="ai-summary-panel"
              >
              @if (isLearningStudioArtifactShellRegistered() && (LSConfig$ | async); as lsConfig) {
                <lsa-artifact-shell-wc
                  [environment]="lsConfig.environment"
                  [person_xid]="lsConfig.personXid"
                  [epub_release_uuid]="lsConfig.releaseUuid"
                  [epub_version]="lsConfig.releaseVersion"
                  [epub_sn]="lsConfig.contextId"
                  [chapter_id]="(currentChapterId$ | async) ?? ''"
                  [token]="lsConfig.customToken"
                  [section_xid]="lsConfig.sectionXid"
                  [org_xid]="lsConfig.orgXid"
                ></lsa-artifact-shell-wc>
              } @else {
                <div class="ai-summary-panel-message">
                  <h3>Something went wrong</h3>
                  <p>We couldn't load the AI summary right now. Please try again in a moment.</p>
                </div>
              }
              </div>
            }
          </div>
        </div>
      </mat-sidenav-content>
    </mat-sidenav-container>
  </ng-container>
</ng-container>

<rdrx-loading-screen *ngIf="loading$ | async"></rdrx-loading-screen>
